import { Optional } from "./types";

type FieldErrors = Optional<{ [key: string]: FieldErrors } | FieldErrors[] | string[]>;

const isBaseErrors = (errors: FieldErrors): errors is string[] =>
  errors instanceof Array && typeof errors[0] === "string";

/**
 * Recursively combines all nested errors on an object that maps to errors
 * (or arrays of errors), and the base case, an array of error messages.
 *
 * Arrays of errors are supported even though there are no use-cases in the
 * code base at the time of writing this to be defensive against default
 * DRF serialization behavior which may produce arrays of nested error fields
 * if `many=True`
 */

export const deepCombineErrors = (errors: FieldErrors): string[] => {
  if (!errors) {
    return [];
  }
  if (isBaseErrors(errors)) {
    return errors;
  }
  return Object.values(errors).flatMap((value) => deepCombineErrors(value));
};

/** Returns the key-value pairs of the most deeply nested errors   */
export const getDeepErrorsObject = (
  errors: FieldErrors,
  fieldName: string = "Unknown field",
): { [key: string]: string[] } => {
  if (!errors || fieldName === "error_code") {
    return {};
  }

  if (isBaseErrors(errors)) {
    return { [fieldName]: errors };
  }

  return Object.entries(errors).reduce(
    (acc, [key, value]) => {
      const deeperObject = getDeepErrorsObject(value, key);
      return { ...acc, ...deeperObject };
    },
    {} as { [key: string]: string[] },
  );
};

/**
 * Flattens all errors (field and non field) from a FieldErrors object into a list of strings
 * to display in the error alert.
 */
export const flattenAllErrors = (errors: FieldErrors): string[] => {
  if (!errors) {
    return [];
  }
  // Ignore the 'data' key when flattening errors.
  // This prevents unintentionally displaying backend custom data in error alerts.
  const errorsWithoutData = Object.fromEntries(
    Object.entries(errors).filter(([key]) => key !== "data"),
  );
  return deepCombineErrors(errorsWithoutData);
};
